import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon } from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import { AdmissionResultRowDTO } from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import { GoalStatusTypes } from '@shared/enums';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import { statusLabel } from './admissionResults.helpers';
import {
  computeYearStats,
  CycleYear,
  downloadCsv,
  GroupStats,
  groupStatsCsv,
  mentorStatsCsv,
  StatLine,
  statLinesCsv,
  statsByContinent,
  statsByMajor,
  statsByMentor,
} from './admissionStats.helpers';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top last:border-b-0';
const leftHead = 'px-4 py-2.5 text-left font-semibold';
const rightHead = 'px-4 py-2.5 text-right font-semibold';
const leftCell = 'px-4 py-2.5 text-left text-customLight';
const rightCell = 'px-4 py-2.5 text-right tabular-nums text-customLight';

const ExportButton = (props: { onClick: () => void; disabled?: boolean }) => (
  <Button
    variant="secondary"
    buttonText="Export CSV"
    icon={<DownloadIcon className="h-4 w-4" />}
    disabled={props.disabled}
    onClick={props.onClick}
  />
);

const cycleLabelOf = (cycle: CycleYear) => (cycle === 'all' ? 'all-years' : String(cycle));

type CycleTabProps = { rows: AdmissionResultRowDTO[]; cycle: CycleYear };

export const CycleSelect = (props: {
  years: number[];
  value: CycleYear;
  onChange: (year: CycleYear) => void;
}) => (
  <FilterField width="w-full tablet:w-52">
    <Select
      label="Admission cycle (graduation year)"
      variant="secondary"
      value={props.value}
      options={[
        { label: 'All years', value: 'all' },
        ...props.years.map((value) => ({ label: String(value), value })),
      ]}
      onSelect={(value) => props.onChange(value === 'all' ? 'all' : Number(value))}
    />
  </FilterField>
);

const StatBlock = (props: { title: string; lines: StatLine[] }) => (
  <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
    <h3 className="text-base font-semibold text-white">{props.title}</h3>
    <dl className="flex flex-col divide-y divide-white/5">
      {props.lines.map((line) => (
        <div key={line.label} className="flex items-baseline justify-between gap-4 py-1.5">
          <dt className="flex flex-col text-sm text-customLight">
            {line.label}
            {line.hint ? <span className="text-[11px] text-customGrey">{line.hint}</span> : null}
          </dt>
          <dd className="shrink-0 text-base font-semibold tabular-nums text-white">
            {line.value ?? '—'}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

export const YearStatsTab = (props: CycleTabProps) => {
  const stats = useMemo(() => computeYearStats(props.rows, props.cycle), [props.rows, props.cycle]);

  const sections = [
    { title: 'Admission rates', lines: stats.admissionRates },
    { title: 'Overall', lines: stats.overall },
    { title: 'US', lines: stats.us },
    { title: 'UK', lines: stats.uk },
    { title: 'EU', lines: stats.eu },
    { title: 'Other', lines: stats.other },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-3xl text-xs text-customGrey">
          Admission rate = offers ÷ decided applications (offers + rejections); pending and
          withdrawn applications are left out. Top 100 uses the QS edition of each student's
          admission cycle
          {stats.rankEditions.length ? ` (here: QS ${stats.rankEditions.join(', QS ')})` : ''}; if
          that edition has not been imported yet, the closest earlier one is used. Ivy, Oxbridge and
          Tier I come from the "University tiers" tab.
        </p>
        <ExportButton
          disabled={!stats.offersCount}
          onClick={() =>
            downloadCsv(
              `admission-stats-${cycleLabelOf(props.cycle)}-${moment().format('YYYY-MM-DD')}.csv`,
              statLinesCsv(sections)
            )
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-4 laptop:grid-cols-2">
        {sections.map((section) => (
          <StatBlock key={section.title} title={section.title} lines={section.lines} />
        ))}
      </div>
    </div>
  );
};

const GroupTable = (props: {
  title: string;
  firstColumn: string;
  groups: GroupStats[];
  fileName: string;
  description?: string;
  filters?: ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <SectionHeading title={props.title} description={props.description} />
      <div className="flex flex-wrap items-end gap-3">
        {props.filters}
        <ExportButton
          disabled={!props.groups.length}
          onClick={() =>
            downloadCsv(
              `${props.fileName}-${moment().format('YYYY-MM-DD')}.csv`,
              groupStatsCsv(props.firstColumn, props.groups)
            )
          }
        />
      </div>
    </div>
    {!props.groups.length ? (
      <EmptyState>No results.</EmptyState>
    ) : (
      <TableShell minWidth="min-w-[960px]">
        <thead>
          <tr className={headRow}>
            <th className={leftHead}>{props.firstColumn}</th>
            <th className={rightHead}>Offers</th>
            <th className={rightHead}>Rejections</th>
            <th className={rightHead}>Applications</th>
            <th className={rightHead}>Students</th>
            <th className={rightHead}>With offers</th>
            <th className={rightHead}>Acceptance</th>
            <th className={rightHead}>Top 100</th>
            <th className={leftHead}>Top universities</th>
          </tr>
        </thead>
        <tbody>
          {props.groups.map((group) => (
            <tr key={group.key} className={bodyRow}>
              <td className={mergeClassNames(leftCell, 'font-semibold text-white')}>
                {group.label}
              </td>
              <td className={rightCell}>{group.offers}</td>
              <td className={rightCell}>{group.rejections}</td>
              <td className={rightCell}>{group.applications}</td>
              <td className={rightCell}>{group.students}</td>
              <td className={rightCell}>{group.studentsWithOffers}</td>
              <td className={rightCell}>
                {group.acceptanceRate === null ? '—' : `${group.acceptanceRate}%`}
              </td>
              <td className={rightCell}>{group.top100}</td>
              <td className={mergeClassNames(leftCell, 'text-xs text-customGrey')}>
                {group.topUniversities.join(' · ') || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    )}
  </div>
);

export const MajorsTab = (props: CycleTabProps) => {
  const groups = useMemo(() => statsByMajor(props.rows), [props.rows]);
  return (
    <GroupTable
      title="Results by major"
      firstColumn="Major"
      fileName={`admission-results-by-major-${cycleLabelOf(props.cycle)}`}
      description="A result with several majors counts once for each major."
      groups={groups}
    />
  );
};

export const ContinentsTab = (props: CycleTabProps) => {
  const groups = useMemo(() => statsByContinent(props.rows), [props.rows]);
  return (
    <GroupTable
      title="Results by continent"
      firstColumn="Continent"
      fileName={`admission-results-by-continent-${cycleLabelOf(props.cycle)}`}
      description="Continent comes from the university's country and QS region."
      groups={groups}
    />
  );
};

const resultTone = (status: number) =>
  status === GoalStatusTypes.ApplicationRejected
    ? 'text-customRed'
    : status === GoalStatusTypes.OfferAccepted
    ? 'text-customGreen'
    : status === GoalStatusTypes.OfferReceived || status === GoalStatusTypes.OfferDeclined
    ? 'text-customPrimary'
    : 'text-customGrey';

export const MentorsTab = (props: CycleTabProps) => {
  const mentors = useMemo(() => statsByMentor(props.rows), [props.rows]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle
      ? mentors.filter((mentor) => mentor.name.toLowerCase().includes(needle))
      : mentors;
  }, [mentors, search]);

  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const cell = (value: ReactNode, className = rightCell) => <td className={className}>{value}</td>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SectionHeading
          title={`Results by mentor · ${visible.length}`}
          description="A student counts for every mentor who held a session with them before the result. Click a mentor to see each student's universities."
        />
        <div className="flex flex-wrap items-end gap-3">
          <FilterField width="w-full tablet:w-64">
            <Select
              label="Mentor"
              variant="secondary"
              showSearch
              allowClear
              value={search || undefined}
              options={mentors.map((mentor) => ({ value: mentor.name, label: mentor.name }))}
              onSelect={(value) => setSearch(value ? String(value) : '')}
              onClear={() => setSearch('')}
              placeholder="All mentors"
            />
          </FilterField>
          <ExportButton
            disabled={!visible.length}
            onClick={() =>
              downloadCsv(
                `admission-results-by-mentor-${cycleLabelOf(props.cycle)}-${moment().format(
                  'YYYY-MM-DD'
                )}.csv`,
                mentorStatsCsv(visible)
              )
            }
          />
        </div>
      </div>
      {!visible.length ? (
        <EmptyState>No mentors with recorded results.</EmptyState>
      ) : (
        <TableShell minWidth="min-w-[1100px]">
          <thead>
            <tr className={headRow}>
              <th className={leftHead}>Mentor</th>
              <th className={rightHead}>Students</th>
              <th className={rightHead}>With offers</th>
              <th className={rightHead}>Offers</th>
              <th className={rightHead}>Rejections</th>
              <th className={rightHead}>Acceptance</th>
              <th className={rightHead}>Top 100</th>
              <th className={rightHead}>Ivy</th>
              <th className={rightHead}>Oxbridge</th>
              <th className={rightHead}>Tier I</th>
              <th className={leftHead}>Courses</th>
              <th className="w-10 px-2 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {visible.map((mentor) => {
              const isOpen = open.has(mentor.id);
              return (
                <Fragment key={mentor.id}>
                  <tr
                    className={mergeClassNames(bodyRow, 'cursor-pointer hover:bg-white/5')}
                    onClick={() => toggle(mentor.id)}
                  >
                    {cell(
                      <span className="font-semibold text-white">{mentor.name}</span>,
                      leftCell
                    )}
                    {cell(mentor.students)}
                    {cell(mentor.studentsWithOffers)}
                    {cell(<span className="font-semibold text-white">{mentor.offers}</span>)}
                    {cell(mentor.rejections)}
                    {cell(mentor.acceptanceRate === null ? '—' : `${mentor.acceptanceRate}%`)}
                    {cell(mentor.top100)}
                    {cell(mentor.ivy)}
                    {cell(mentor.oxbridge)}
                    {cell(mentor.tier1)}
                    {cell(
                      <span className="text-xs text-customGrey">{mentor.courses.join(', ')}</span>,
                      leftCell
                    )}
                    <td className="px-2 py-2.5 text-customGrey">
                      {isOpen ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr className={bodyRow}>
                      <td colSpan={12} className="bg-black/20 px-6 py-4">
                        <div className="grid grid-cols-1 gap-4 laptop:grid-cols-2">
                          {mentor.studentResults.map((student) => (
                            <div
                              key={student.studentId}
                              className="flex flex-col gap-1.5 rounded-xl bg-white/5 p-3"
                            >
                              <span className="text-sm font-semibold text-white">
                                {student.studentName}
                                <span className="ml-2 text-xs font-normal text-customGrey">
                                  {student.graduationYear
                                    ? `Class of ${student.graduationYear} · `
                                    : ''}
                                  {student.sessions} session{student.sessions === 1 ? '' : 's'}
                                </span>
                              </span>
                              <ul className="flex flex-col gap-0.5 text-xs">
                                {student.results.map((result, index) => (
                                  <li
                                    key={index}
                                    className="flex items-center justify-between gap-3"
                                  >
                                    <span className="text-customLight">
                                      {result.university}
                                      <span className="text-customGrey"> · {result.country}</span>
                                      {result.top100 ? (
                                        <span className="ml-1 rounded bg-customGold/15 px-1 text-[10px] text-customGold">
                                          Top 100
                                        </span>
                                      ) : null}
                                    </span>
                                    <span
                                      className={mergeClassNames(
                                        'shrink-0',
                                        resultTone(result.status)
                                      )}
                                    >
                                      {statusLabel(result.status)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </div>
  );
};
